import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { firestore } from "../firebase";
import { debugLogError } from "../lib/debug-error-log";
import { mapDocToUserProfile } from "../services/profileService";
import { UserProfile } from "../types/Profile";

export function useUserProfile(userUuid: string | null | undefined) {
  const [initialized, setInitialized] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userUuid) {
      setProfile(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const userProfileDocRef = doc(firestore, "profiles", userUuid);

    const unsubscribe = onSnapshot(
      userProfileDocRef,
      (docSnapshot) => {
        setInitialized(true);

        if (docSnapshot.exists()) {
          setProfile(mapDocToUserProfile(userUuid, docSnapshot.data()));
        } else {
          setProfile(null);
          setError("User profile not found.");
        }
        setLoading(false);
      },
      (error) => {
        debugLogError("useUserProfile::onSnapshot", error);
        setError("Failed to fetch profile.");
        setProfile(null);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [userUuid]);

  return useMemo(
    () => ({
      profile,
      loading: (loading && !!userUuid) || !initialized,
      error,
    }),
    [profile, loading, error, userUuid, initialized],
  );
}
